import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { DataTable, Column, ProgressSpinner, SCIcon, Dialog, ProgressBar } from '../../Common/Imports';
import { ApiResponse, User_EXT } from '../../Models/Classes';
import UserNew from './Users_New';
import UserEdit from './Users_Edit';
import { PermissionHelper, usePageID, usePermissions } from '../../Helpers/Permissions/PermissionContext';
import { FormatDateTimeFromDate } from '../../Common/Utility/FormatDateTimeFromDate';
interface StakeholderUsersProps {
    stakeholderId: number;
}

export const UserList: React.FC<StakeholderUsersProps> = ({ stakeholderId }) => {
    const { pageID } = usePageID();
    const PAGENAME = "-USERS";
    const P_Users_Create = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 1 };
    const P_Users_Read = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 2 };
    const P_Users_Update = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 3 };
    const P_Users_SendResetMail = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 5 };

    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<User_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [editData, setEditData] = useState<User_EXT | undefined>();
    const [CreateNewData, setCreateNewData] = useState<boolean>(false);
    const [LoaderSendResetData, setLoaderSendResetData] = useState<boolean>(false);

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<User_EXT[]>>(`User/GetListByIdStakeholder/${stakeholderId}`);
            if (resp.data.code == 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    created: item.created ? new Date(item.created) : undefined,
                    lastLogin: item.lastLogin ? new Date(item.lastLogin) : undefined,
                    updated: item.updated ? new Date(item.updated) : undefined
                }));
                SetTableData(processedData);
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, showToast, stakeholderId, t]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const updateStatus = async (data: User_EXT) => {
        data.isActive = !data.isActive;
        try {
            const resp = await AxiosHelper.post<ApiResponse<User_EXT>>(`User/UpdateUser/`, data);
            if (resp.data.code == 0) {
                showToast('success', 'Success', t("Common.Toasts.New.Success"));
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            GetData();
        }
    }

    const activeBody = (data: User_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Users_Update}>
                {!data.isActive ?
                    <SCIcon Name="Lock" Classes="text-danger" Pointer OnClick={() => updateStatus(data)} />
                    :
                    <SCIcon Name="Unlock" Classes="text-success" Pointer OnClick={() => updateStatus(data)} />
                }
            </PermissionHelper>)
    }

    const sendReset = async (data: User_EXT) => {
        setLoaderSendResetData(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<User_EXT>>(`Auth/ForgotPassword/` + data.email);
            if (resp.data.code == 0) {
                GetData();
                showToast('success', 'Success', t("User.Toast.SendResetMail.SentSucces"));
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.Update.Error"));
        } finally {
            setLoaderSendResetData(false);
        }
    }

    const mailBody = (data: User_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Users_SendResetMail}>
                {
                    LoaderSendResetData ?
                        <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration=".5s" />
                        :
                        <SCIcon Name="Mail" Classes="text-info" Pointer OnClick={() => sendReset(data)} />
                }
            </PermissionHelper>
        );
    }

    const openEdit = (data: User_EXT) => {
        setEditData(data);
    }

    const editBody = (data: User_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Users_Update}>
                <SCIcon Name="Edit" Classes="text-secondary" Pointer OnClick={() => openEdit(data)} />
            </PermissionHelper>)
    }

    return (
        <PermissionHelper requiredPermission={P_Users_Read}>
            {
                CreateNewData &&
                <Dialog
                    header="New User"
                    visible={CreateNewData ? true : false}
                    onHide={() => { setCreateNewData(false); GetData() }}>
                    <UserNew stakeholderId={stakeholderId} OnSave={() => { setCreateNewData(false); GetData() }} />
                </Dialog>
            }
            {
                editData &&
                <Dialog
                    header="Edit User"
                    visible={editData ? true : false}
                    onHide={() => { setEditData(undefined); GetData() }}>
                    <UserEdit email={editData.email} OnSave={() => { setEditData(undefined); GetData() }} />
                </Dialog>
            }
            <PermissionHelper requiredPermission={P_Users_Create}>
                <div className="col d-flex justify-content-end">
                    <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => setCreateNewData(true)}></SCIcon>
                </div>
            </PermissionHelper>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        value={TableData}
                        size="small"
                        stripedRows>
                        <Column
                            field="name"
                            header={t("User.List.Name")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="surname"
                            header={t("User.List.Surname")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="email"
                            header={t("User.List.Email")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="emailConfirmed"
                            header={t("User.List.EmailConfirmed")}
                            body={(e: User_EXT) => { const _T = e.emailConfirmed ? t("Common.Booleans.Yes") : t("Common.Booleans.No"); return _T }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="created"
                            header={t("User.List.Created")}
                            body={(e: User_EXT) => FormatDateTimeFromDate(e.created, { withTime: true })}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="lastLogin"
                            header={t("User.List.LastLogin")}
                            body={(e: User_EXT) => FormatDateTimeFromDate(e.lastLogin, { withTime: true })}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="updated"
                            header={t("User.List.Updated")}
                            body={(e: User_EXT) => FormatDateTimeFromDate(e.updated, { withTime: true })}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="isActive"
                            header={t("User.List.Status")}
                            style={{ maxWidth: '150px!important', width: "150px" }}
                            body={(e: User_EXT) => { const _T = e.isActive ? t("Common.Booleans.Active") : t("Common.Booleans.NotActive"); return _T }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            body={(e: User_EXT) => mailBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            body={(e: User_EXT) => editBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="active"
                            body={(e: User_EXT) => activeBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </PermissionHelper>
    );
};

export default UserList;
